import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { PackageSearch, Search, ArrowLeft, CheckCircle, Clock, Package, ExternalLink, Truck } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/* ─ animations ─ */
const fadeUp = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;

/* ─ Layout ─ */
const Page  = styled.div`background:#FFFDF7; min-height:100vh; padding-top:90px;`;
const Wrap  = styled.div`max-width:640px; margin:0 auto; padding:48px 24px 120px;`;

const BackLink = styled(Link)`
  display:inline-flex; align-items:center; gap:6px;
  font-size:.78rem; letter-spacing:.1em; text-transform:uppercase;
  color:rgba(26,18,8,.45); text-decoration:none; margin-bottom:32px;
  transition:color .2s;
  &:hover{color:#7C3A00;}
`;

const Eyebrow = styled.p`
  font-family:'Raleway',serif; font-size:.65rem; letter-spacing:.2em;
  color:#C07020; margin:0 0 8px;
`;
const H1 = styled.h1`
  font-family:'Cormorant Garamond',serif; font-size:clamp(2rem,4vw,2.8rem);
  font-weight:400; color:#1A1208; margin:0 0 10px;
`;
const Sub = styled.p`
  font-size:.9rem; color:rgba(26,18,8,.55); margin:0 0 36px; line-height:1.6;
`;

/* ─ Form ─ */
const FormCard = styled.div`
  background:#FAF3E0; border:1.5px solid #E8DFD0; border-radius:16px;
  padding:28px 28px 24px;
  animation:${fadeUp} .25s ease;
`;
const Label = styled.label`
  display:block; font-size:.72rem; letter-spacing:.1em; text-transform:uppercase;
  color:rgba(26,18,8,.5); font-weight:600; margin-bottom:7px;
`;
const Input = styled.input`
  width:100%; padding:12px 16px; border:1.5px solid #E8DFD0; border-radius:10px;
  font-size:.95rem; color:#1A1208; background:#FFFDF7; outline:none;
  transition:border-color .2s;
  &:focus{border-color:#7C3A00; box-shadow:0 0 0 3px rgba(124,58,0,.07);}
  &::placeholder{color:rgba(26,18,8,.3);}
`;
const FieldGroup = styled.div`display:flex; flex-direction:column; gap:18px; margin-bottom:22px;`;

const SearchBtn = styled.button`
  width:100%; padding:13px;
  background:#7C3A00; color:#FFFDF7;
  border:none; border-radius:10px;
  font-size:.95rem; font-weight:600; letter-spacing:.04em;
  cursor:pointer; transition:background .2s;
  display:flex; align-items:center; justify-content:center; gap:8px;
  &:hover:not(:disabled){background:#A04A00;}
  &:disabled{opacity:.55; cursor:default;}
`;
const ErrorMsg = styled.div`
  color:#C0392B; font-size:.88rem; padding:10px 14px;
  background:rgba(192,57,43,.07); border-radius:8px; margin-bottom:4px;
`;

/* ─ Result ─ */
const ResultWrap = styled.div`animation:${fadeUp} .28s ease;`;

const ResultHeader = styled.div`
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  margin-bottom:20px;
`;
const OrderCode = styled.span`
  font-family:'Courier New',monospace; font-size:1.4rem; font-weight:700; color:#7C3A00;
`;
const StatusBadge = styled.span`
  display:inline-flex; align-items:center; gap:5px;
  padding:5px 14px; border-radius:999px; font-size:.78rem; font-weight:700;
  background:${({$paid})=>$paid?'#d1fae5':'#fef3c7'};
  color:${({$paid})=>$paid?'#065f46':'#92400e'};
  border:1px solid ${({$paid})=>$paid?'#6ee7b7':'#fcd34d'};
  text-transform:capitalize;
`;

const InfoGrid = styled.div`
  display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:14px;
  @media(max-width:500px){grid-template-columns:1fr;}
`;
const InfoCard = styled.div`
  background:#FFFDF7; border:1.5px solid #E8DFD0; border-radius:12px; padding:14px 16px;
`;
const InfoTitle = styled.div`
  font-size:.65rem; letter-spacing:.1em; text-transform:uppercase;
  color:rgba(26,18,8,.4); font-weight:600; margin-bottom:8px;
`;
const InfoValue = styled.div`font-size:.9rem; color:#1A1208; font-weight:500; line-height:1.5;`;

const ProductsCard = styled.div`
  background:#FFFDF7; border:1.5px solid #E8DFD0; border-radius:12px; overflow:hidden; margin-bottom:20px;
`;
const ProductsTitle = styled.div`
  font-size:.65rem; letter-spacing:.1em; text-transform:uppercase;
  color:rgba(26,18,8,.4); font-weight:600; padding:12px 16px;
  border-bottom:1px solid #E8DFD0;
`;
const ProductRow = styled.div`
  display:flex; align-items:center; gap:12px; padding:12px 16px;
  border-bottom:1px solid rgba(232,223,208,.5);
  &:last-child{border-bottom:none;}
`;

const ResetBtn = styled.button`
  background:none; border:1.5px solid #E8DFD0; color:rgba(26,18,8,.55);
  border-radius:10px; padding:10px 20px; cursor:pointer; font-size:.88rem;
  transition:all .2s;
  &:hover{border-color:#7C3A00; color:#7C3A00;}
`;

const TrackCard = styled.div`
  background:${({$active})=>$active?'#F0FBF4':'#FFFDF7'};
  border:1.5px solid ${({$active})=>$active?'#B7DFCA':'#E8DFD0'};
  border-radius:12px; padding:18px 20px; margin-bottom:14px;
`;
const TrackRow = styled.div`display:flex; align-items:flex-start; gap:14px;`;
const TrackIcon = styled.div`
  width:38px; height:38px; border-radius:10px; flex-shrink:0;
  background:${({$active})=>$active?'#D1FAE5':'#F5EFE6'};
  color:${({$active})=>$active?'#065f46':'#7C3A00'};
  display:flex; align-items:center; justify-content:center;
`;
const TrackInfo = styled.div`flex:1;`;
const TrackLabel = styled.div`
  font-size:.65rem; letter-spacing:.1em; text-transform:uppercase;
  color:rgba(26,18,8,.4); font-weight:600; margin-bottom:4px;
`;
const TrackNumber = styled.div`
  font-family:'Courier New',monospace; font-size:1rem; font-weight:700;
  color:#1A1208; word-break:break-all;
`;
const TrackStatus = styled.span`
  display:inline-block; padding:3px 10px; border-radius:999px; font-size:.72rem; font-weight:700;
  margin-top:6px;
  background:${({$s})=>
    $s==='DELIVERED'?'#d1fae5':
    $s==='IN_TRANSIT'?'#dbeafe':
    $s==='TRANSIT'?'#dbeafe':
    '#fef3c7'};
  color:${({$s})=>
    $s==='DELIVERED'?'#065f46':
    $s==='IN_TRANSIT'?'#1e40af':
    $s==='TRANSIT'?'#1e40af':
    '#92400e'};
`;
const TrackLink = styled.a`
  display:inline-flex; align-items:center; gap:5px; margin-top:10px;
  padding:8px 16px; border-radius:8px;
  background:#7C3A00; color:#FFFDF7;
  font-size:.8rem; font-weight:700; letter-spacing:.05em; text-decoration:none;
  transition:background .2s;
  &:hover{background:#C07020;}
`;

export default function TrackOrderPage() {
  const { lang } = useLanguage();
  const { region } = useRegion();
  const [searchParams] = useSearchParams();

  const [code,    setCode]    = useState(searchParams.get('code') || '');
  const [email,   setEmail]   = useState(searchParams.get('email') || '');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [order,   setOrder]   = useState(null);

  const es = lang === 'es';

  const handleSearch = async () => {
    if (!code.trim() || !email.trim()) {
      setError(es ? 'Ingresa el código de pedido y tu correo.' : 'Enter your order code and email.');
      return;
    }
    setLoading(true); setError(''); setOrder(null);
    try {
      const r = await fetch(`${API}/api/orders/track?code=${encodeURIComponent(code.trim())}&email=${encodeURIComponent(email.trim())}`);
      const data = await r.json();
      if (!r.ok) setError(data.error || (es ? 'Pedido no encontrado.' : 'Order not found.'));
      else setOrder(data);
    } catch {
      setError(es ? 'Error de conexión. Intenta más tarde.' : 'Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = s => s ? new Date(s).toLocaleDateString(es ? 'es-MX' : 'en-US', {
    day: '2-digit', month: 'long', year: 'numeric',
  }) : '—';
  const fmtAddr = raw => {
    if (!raw) return '—';
    let a = raw;
    if (typeof a === 'string') { try { a = JSON.parse(a); } catch { return raw; } }
    // Soporta tanto {line1, city, state, postal} como {street1, city, state, zip}
    return [
      a.name,
      a.line1   || a.street1,
      a.line2   || a.street2,
      a.city,
      a.state,
      a.postal  || a.zip || a.postal_code,
      a.country,
    ].filter(Boolean).join(', ');
  };
  const fmtTotal = o => {
    const sym = o.currency === 'mxn' ? 'MX$' : '$';
    return o.amount_total ? `${sym}${(o.amount_total / 100).toFixed(2)}` : '—';
  };
  const items = Array.isArray(order?.items) ? order.items : [];

  return (
    <Page>
      <Wrap>
        <BackLink to={`/${region}/store`}>
          <ArrowLeft size={13} />
          {es ? 'Volver a la tienda' : 'Back to store'}
        </BackLink>

        <Eyebrow>{es ? 'Hacienda Real' : 'Hacienda Real'}</Eyebrow>
        <H1>{es ? 'Rastrear pedido' : 'Track your order'}</H1>
        <Sub>
          {es
            ? 'Ingresa el código de pedido que recibiste en tu correo y tu dirección de correo electrónico.'
            : 'Enter the order code from your confirmation email along with your email address.'}
        </Sub>

        {!order ? (
          <FormCard>
            <FieldGroup>
              <div>
                <Label htmlFor="track-code">
                  {es ? 'Código de pedido' : 'Order code'}
                </Label>
                <Input
                  id="track-code"
                  placeholder="HR-260422-A3K9"
                  value={code}
                  onChange={e => setCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <div>
                <Label htmlFor="track-email">
                  {es ? 'Correo electrónico' : 'Email address'}
                </Label>
                <Input
                  id="track-email"
                  type="email"
                  placeholder={es ? 'tu@correo.com' : 'you@email.com'}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                />
              </div>
            </FieldGroup>
            {error && <ErrorMsg>{error}</ErrorMsg>}
            <SearchBtn onClick={handleSearch} disabled={loading}>
              {loading
                ? (es ? 'Buscando…' : 'Searching…')
                : <><Search size={15} />{es ? 'Buscar pedido' : 'Search order'}</>}
            </SearchBtn>
          </FormCard>
        ) : (
          <ResultWrap>
            <ResultHeader>
              <OrderCode>{order.order_code}</OrderCode>
              <StatusBadge $paid={order.status === 'paid'}>
                {order.status === 'paid'
                  ? <><CheckCircle size={12} />{es ? 'Pagado' : 'Paid'}</>
                  : <><Clock size={12} />{order.status}</>}
              </StatusBadge>
            </ResultHeader>

            <InfoGrid>
              <InfoCard>
                <InfoTitle>{es ? 'Fecha' : 'Date'}</InfoTitle>
                <InfoValue>{fmtDate(order.created_at)}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoTitle>{es ? 'Total' : 'Total'}</InfoTitle>
                <InfoValue style={{ fontSize:'1.15rem', fontWeight:700, color:'#7C3A00' }}>
                  {fmtTotal(order)}
                </InfoValue>
              </InfoCard>
              <InfoCard style={{ gridColumn:'1 / -1' }}>
                <InfoTitle>{es ? 'Dirección de envío' : 'Shipping address'}</InfoTitle>
                <InfoValue>{fmtAddr(order.shipping_address)}</InfoValue>
              </InfoCard>
            </InfoGrid>

            {/* ── Tracking ── */}
            {order.tracking_number ? (
              <TrackCard $active={order.tracking_status === 'DELIVERED' || order.tracking_status === 'IN_TRANSIT' || order.tracking_status === 'TRANSIT'}>
                <TrackRow>
                  <TrackIcon $active={!!order.tracking_status && order.tracking_status !== 'UNKNOWN'}>
                    <Truck size={18} />
                  </TrackIcon>
                  <TrackInfo>
                    <TrackLabel>{es ? 'Número de rastreo' : 'Tracking number'} · {order.tracking_carrier || ''}</TrackLabel>
                    <TrackNumber>{order.tracking_number}</TrackNumber>
                    {order.tracking_status && order.tracking_status !== 'UNKNOWN' && (
                      <TrackStatus $s={order.tracking_status}>
                        {order.tracking_status === 'DELIVERED' ? (es ? '✅ Entregado' : '✅ Delivered') :
                         order.tracking_status === 'IN_TRANSIT' || order.tracking_status === 'TRANSIT' ? (es ? '🚚 En camino' : '🚚 In transit') :
                         order.tracking_status === 'PRE_TRANSIT' ? (es ? '📦 Preparando envío' : '📦 Pre-transit') :
                         order.tracking_status}
                      </TrackStatus>
                    )}
                    {order.tracking_status === 'UNKNOWN' && (
                      <TrackStatus $s="UNKNOWN">{es ? '⏳ Pendiente de actualización' : '⏳ Awaiting update'}</TrackStatus>
                    )}
                    {order.tracking_url && (
                      <div style={{ marginTop:10 }}>
                        <TrackLink href={order.tracking_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink size={13} />
                          {es ? 'Rastrear en el sitio del carrier' : 'Track on carrier website'}
                        </TrackLink>
                      </div>
                    )}
                  </TrackInfo>
                </TrackRow>
              </TrackCard>
            ) : order.status === 'paid' ? (
              <TrackCard>
                <TrackRow>
                  <TrackIcon>
                    <Package size={18} />
                  </TrackIcon>
                  <TrackInfo>
                    <TrackLabel>{es ? 'Estado del envío' : 'Shipping status'}</TrackLabel>
                    <div style={{ fontSize:'.9rem', color:'rgba(26,18,8,.6)', marginTop:2 }}>
                      {es ? 'Tu pedido está siendo procesado. El número de rastreo aparecerá aquí pronto.' : 'Your order is being processed. The tracking number will appear here soon.'}
                    </div>
                  </TrackInfo>
                </TrackRow>
              </TrackCard>
            ) : null}

            {items.length > 0 && (
              <ProductsCard>
                <ProductsTitle>
                  {es ? `Productos (${items.length})` : `Products (${items.length})`}
                </ProductsTitle>
                {items.map((item, i) => (
                  <ProductRow key={i}>
                    {item.image && (
                      <img src={item.image} alt={item.name}
                        style={{ width:44, height:44, borderRadius:8, objectFit:'cover', background:'#F5EFE6', flexShrink:0 }} />
                    )}
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, color:'#1A1208', fontSize:'.92rem' }}>{item.name}</div>
                      {item.description && (
                        <div style={{ fontSize:'.78rem', color:'rgba(26,18,8,.45)', marginTop:2 }}>{item.description}</div>
                      )}
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:'.78rem', color:'rgba(26,18,8,.4)' }}>×{item.quantity}</div>
                      {item.price != null && (
                        <div style={{ fontWeight:700, color:'#7C3A00', fontSize:'.88rem' }}>
                          {order.currency === 'mxn' ? 'MX$' : '$'}{(item.price / 100).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </ProductRow>
                ))}
              </ProductsCard>
            )}

            <ResetBtn onClick={() => { setOrder(null); setCode(''); setEmail(''); }}>
              ← {es ? 'Buscar otro pedido' : 'Search another order'}
            </ResetBtn>
          </ResultWrap>
        )}
      </Wrap>
    </Page>
  );
}
